import React, { useCallback, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classnames from 'classnames';
import noop from 'lodash/noop';
import moment from 'moment';
import { useAtomValue } from 'jotai';
import { dodAtom } from '@components/metrics-panel-hz/atoms';
import DatePicker from '@components/common/DatePicker';
import { getComparisonDisabledDate, calcComparisonInterval } from './utils/dates';
import DatePickerWithOption from './DatePickerWithOptions';
import useShowLiveMode from './hooks/useShowLiveMode';
import { LIVE_MODE_HELP_TEXT_APP, LIVE_MODE_HELP_TEXT_VSI } from './liveModeHelpText';
import { isAppModule } from '@utils/constants';
import styles from './index.less';
import {
  setComparisonInterval,
  setComparisonUnavailable,
} from '@store/summaryContainer/comparison/actions';

const DatePickers = ({
  interval,
  comparisonInterval,
  validateUpdateInterval,
  summaryDatePickerRef,
  comparisonDatePickerRef,
  durationAllowed,
}) => {
  const dispatch = useDispatch();
  const isLiveModeEnabled = useShowLiveMode();

  // const isNaDrilldownPage = window.location.pathname.includes('na-drilldown');
  // const isComparePage = window.location.pathname.includes('compare');

  const { from, to, shortcut } = interval;
  const { from: comparisonFrom, to: comparisonTo } = comparisonInterval;
  const [summaryInterval, setSummaryInterval] = useState([from, to, shortcut]);
  const [compInterval, setCompInterval] = useState([comparisonFrom, comparisonTo]);
  const [validComparison, setValidComparison] = useState(comparisonFrom && comparisonTo);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const fixedRange = useMemo(
    () => moment(summaryInterval[1]).diff(moment(summaryInterval[0]), 'minutes'),
    [summaryInterval],
  );
  // this will replace the selector state as dod will not persist
  // and will always default to false
  const dodEnabled = useAtomValue(dodAtom);

  const datePickerClassName = classnames(styles.summaryDatePicker, { dod: dodEnabled });

  const onCancel = useCallback(
    () => {
      if (summaryDatePickerRef.current) {
        setSummaryInterval([from, to, shortcut]);
        summaryDatePickerRef.current.reset();
      }

      if (comparisonDatePickerRef.current) {
        setCompInterval([comparisonFrom, comparisonTo]);
        comparisonDatePickerRef.current.reset();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      from,
      to,
      shortcut,
      comparisonFrom,
      comparisonTo,
      comparisonDatePickerRef.current,
      summaryDatePickerRef.current,
    ],
  );

  const openSummaryDatePicker = useCallback(
    () => {
      if (comparisonDatePickerRef.current) {
        comparisonDatePickerRef.current.close();
      }
      setSummaryOpen(true);
      setComparisonOpen(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const closeSummaryDatePicker = useCallback(() => {
    setSummaryOpen(false);
  }, []);

  const changeSummaryDatePicker = useCallback(
    ({ interval: updatedInterval, selectedShortcut }) => {
      setSummaryInterval([...updatedInterval, selectedShortcut]);
      const { from: compFrom, to: compTo } = calcComparisonInterval(
        {
          from: updatedInterval[0],
          to: updatedInterval[1],
          shortcut: selectedShortcut,
        },
        durationAllowed,
      );
      if (compFrom && compTo) {
        setCompInterval([compFrom.format(), compTo.format()]);
        if (comparisonDatePickerRef.current) {
          comparisonDatePickerRef.current.setDate([compFrom, compTo]);
        }
      } else {
        setCompInterval(['', '']);
      }
      setValidComparison(compFrom && compTo);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const changeComparisonDatePicker = useCallback(({ interval: newInterval }) => {
    setCompInterval([newInterval[0].format(), newInterval[1].format()]);
  }, []);

  const openComparisonDatePicker = useCallback(
    () => {
      summaryDatePickerRef.current.close();
      setSummaryOpen(false);
      setComparisonOpen(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const closeComparisonDatePicker = useCallback(() => {
    setComparisonOpen(false);
  }, []);

  const onApply = useCallback(
    () => {
      // const isLiveMode = summaryInterval[2] === DATE_SHORTCUTS.last15Mins;
      // dispatch(setRealTimeMode(isLiveMode));
      // TODO: a saga action can be used to setup live mode
      setComparisonOpen(false);
      setSummaryOpen(false);
      validateUpdateInterval(
        [moment(summaryInterval[0]), moment(summaryInterval[1])],
        summaryInterval[2],
      );
      if (validComparison) {
        dispatch(setComparisonInterval(...compInterval));
      } else {
        dispatch(setComparisonUnavailable());
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [compInterval, summaryInterval],
  );

  return (
    <div className={styles.datePickers}>
      {dodEnabled && validComparison && (
        <>
          <DatePicker
            ref={comparisonDatePickerRef}
            className={styles.comparisonDatePicker}
            title="Comparison"
            expanded={summaryOpen}
            defaultValue={[moment(compInterval[0]), moment(compInterval[1])]}
            disabledDate={getComparisonDisabledDate(summaryInterval, durationAllowed)}
            onApply={onApply}
            onOpen={openComparisonDatePicker}
            onCancel={onCancel}
            onClose={closeComparisonDatePicker}
            onChange={changeComparisonDatePicker}
            fixedRange={fixedRange}
            durationAllowed={durationAllowed}
          />
        </>
      )}
      {dodEnabled && !validComparison && (
        <div className={styles.unavailable}>Comparison Unavailable</div>
      )}
      {dodEnabled && (
        <div style={{ display: 'flex', padding: '0px 2px', alignItems: 'center' }}>vs</div>
      )}
      <DatePickerWithOption
        durationAllowed={durationAllowed}
        title="Time Frame"
        ref={summaryDatePickerRef}
        expanded={comparisonOpen}
        defaultValue={[moment(summaryInterval[0]), moment(summaryInterval[1])]}
        className={datePickerClassName}
        defaultShortcut={shortcut}
        onApply={onApply}
        onOpen={openSummaryDatePicker}
        onCancel={onCancel}
        onClose={closeSummaryDatePicker}
        onChange={changeSummaryDatePicker}
        liveMode={isLiveModeEnabled}
        liveMenuTooltip={
          isLiveModeEnabled
            ? isAppModule
              ? LIVE_MODE_HELP_TEXT_APP
              : LIVE_MODE_HELP_TEXT_VSI
            : undefined
        }
      />
    </div>
  );
};

DatePickers.defaultProps = {
  setComparisonInterval: noop,
  setComparisonUnavailable: noop,
  summaryDatePickerRef: {},
  validateUpdateInterval: noop,
  comparisonDatePickerRef: {},
};

export default React.memo(DatePickers);
